/**
 * A voice note, played where it was said (owner's prototype, 2026-08-22).
 *
 * Play, pause, how far through it is and how long it runs. The bars are the
 * prototype's — they animate while it plays and stand still when it does not,
 * and they are decoration: they are not a waveform of these bytes and do not
 * pretend to be, which is why the progress is a real bar underneath them
 * rather than the bars lighting up in sequence.
 *
 * THE LENGTH COMES FROM THE SERVER, NOT FROM THE ELEMENT. A WebM written by a
 * browser's recorder has no duration in its header — the recorder is streaming
 * and does not know the length until it stops — so `audio.duration` reads
 * `Infinity` for exactly the files this feature produces. The recorder watched
 * a clock and the figure it read is stored beside the file; the element's own
 * duration is used only when it turns out to be a finite number, which is the
 * case for a file that arrived some other way.
 */
import { useEffect, useRef, useState, type CSSProperties } from "react";

export const PLAY_LABEL = "Play";
export const PAUSE_LABEL = "Pause";

/** `7200` → `"0:07"`. Never a decimal: a voice note is not a stopwatch. */
export function formatDuration(ms: number | null): string {
  if (ms === null || !Number.isFinite(ms) || ms <= 0) return "0:00";
  const total = Math.round(ms / 1000);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

const FOCUS =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/70 " +
  "focus-visible:ring-offset-1";

function PlayGlyph() {
  return (
    <svg viewBox="0 0 20 20" className="ml-0.5 h-3.5 w-3.5" aria-hidden="true">
      <path d="M6.5 4.5 15 10l-8.5 5.5z" fill="currentColor" />
    </svg>
  );
}

function PauseGlyph() {
  return (
    <svg viewBox="0 0 20 20" className="h-3.5 w-3.5" aria-hidden="true">
      <path d="M6 4.5h2.5v11H6zM11.5 4.5H14v11h-2.5z" fill="currentColor" />
    </svg>
  );
}

/** The five bars. Decoration, and marked as such. */
export function Waveform({ playing }: { playing: boolean }) {
  return (
    <span className="nob-wave" data-playing={playing ? "true" : "false"} aria-hidden="true">
      {[0, 1, 2, 3, 4].map((i) => (
        <i key={i} style={{ "--i": i } as CSSProperties} />
      ))}
    </span>
  );
}

export function AudioBubble({
  src,
  durationMs,
  label,
}: {
  src: string;
  /** What the recorder clocked, in ms, or null when nobody said. */
  durationMs: number | null;
  /** What this clip IS, for anyone not looking at it. */
  label: string;
}) {
  const audio = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  /** The element's own duration, if it ever turns out to be a real number. */
  const [measured, setMeasured] = useState<number | null>(null);

  // A bubble that scrolls out of the thread, or a thread that is left, must not
  // keep talking. React removes the element, which stops it — this is the
  // belt-and-braces for the case where it is reused instead.
  useEffect(() => {
    const el = audio.current;
    return () => el?.pause();
  }, []);

  const total = durationMs ?? measured;
  const ratio = total && total > 0 ? Math.min(1, (elapsed * 1000) / total) : 0;
  // Counting down while it plays, and stating the length while it does not —
  // the way every voice note anyone has ever received behaves.
  const shown = playing && total ? formatDuration(total - elapsed * 1000) : formatDuration(total);

  return (
    <span className="nob-audio" data-testid="audio-bubble">
      <audio
        ref={audio}
        src={src}
        preload="metadata"
        data-testid="audio-el"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          setElapsed(0);
        }}
        onTimeUpdate={(e) => setElapsed(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => {
          const d = e.currentTarget.duration;
          // `Infinity` is the normal reading for a recorded WebM. Keep null.
          if (Number.isFinite(d) && d > 0) setMeasured(d * 1000);
        }}
      />
      <button
        type="button"
        aria-label={playing ? PAUSE_LABEL : PLAY_LABEL}
        onClick={(event) => {
          // The bubble around this one opens a menu on press; playing a clip
          // is not asking for that menu.
          event.stopPropagation();
          const el = audio.current;
          if (!el) return;
          if (el.paused) void el.play()?.catch(() => setPlaying(false));
          else el.pause();
        }}
        className={FOCUS}
      >
        {playing ? <PauseGlyph /> : <PlayGlyph />}
      </button>

      <span className="nob-audio-body">
        <Waveform playing={playing} />
        <span
          className="nob-audio-track"
          role="progressbar"
          aria-label={label}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(ratio * 100)}
        >
          <i style={{ width: `${ratio * 100}%` }} />
        </span>
      </span>

      <span className="nob-audio-time tabular-nums">{shown}</span>
    </span>
  );
}
